'use client';

import { useState } from 'react';
import { useLocale } from 'next-intl';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { SectionKicker } from '@/components/brand/section-kicker';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { TypographyH2, TypographyLead, TypographyMuted } from '@/components/ui/typography';
import { Reveal } from '@/components/motion/reveal';
import type { BlogCategory } from '@/content/blog-types';
import { BlogPostCard, type BlogCard } from '@/components/sections/blog-post-card';
import {
  BlogTopicFilterButton,
  type BlogTopic,
} from '@/components/sections/blog-topic-filter-button';

export type { BlogCard };
export type { BlogTopic };

interface BlogTeaserClientProps {
  posts: BlogCard[];
  topics: BlogTopic[];
  labels: {
    overline: string;
    heading: string;
    subheading: string;
    viewAll: string;
    empty: string;
  };
}

export function BlogTeaserClient({ posts, topics, labels }: BlogTeaserClientProps) {
  const [active, setActive] = useState<'all' | BlogCategory>('all');
  const locale = useLocale();
  // Homepage teaser shows the latest three; the filter reveals a topic's posts.
  const visible = active === 'all' ? posts.slice(0, 3) : posts.filter((p) => p.category === active);

  return (
    <section id="blog" className="section relative isolate overflow-hidden">
      <div className="container-brand relative">
        {/* Decorations — pinned to the container edges so they hug the title.
            Mirror with the page in RTL so they keep the same visual corners. */}
        <Image
          src="/blog-deco-topleft.webp"
          alt=""
          aria-hidden
          width={755}
          height={860}
          sizes="260px"
          className="pointer-events-none absolute -top-6 start-0 -z-10 hidden w-[180px] select-none lg:block lg:w-[240px] rtl:-scale-x-100"
        />
        <Image
          src="/blog-deco-topright.webp"
          alt=""
          aria-hidden
          width={820}
          height={808}
          sizes="260px"
          className="pointer-events-none absolute -top-4 end-0 -z-10 hidden w-[180px] select-none lg:block lg:w-[240px] rtl:-scale-x-100"
        />

        {/* Header */}
        <Reveal className="mx-auto max-w-3xl text-center">
          <SectionKicker>{labels.overline}</SectionKicker>
          <TypographyH2 className="mt-5 border-b-0 pb-0 font-display font-medium text-primary">
            {labels.heading}
          </TypographyH2>
          <TypographyLead className="mx-auto mt-4 max-w-xl">{labels.subheading}</TypographyLead>
        </Reveal>

        {/* Topic filter — real buttons so they carry the brand button gesture */}
        <Reveal index={1} className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {topics.map((topic) => (
            <BlogTopicFilterButton
              key={topic.key}
              topic={topic}
              active={active === topic.key}
              onSelect={setActive}
              className="h-auto gap-2 px-5 py-2.5"
            />
          ))}
        </Reveal>

        {/* Cards — three across on desktop. Below lg (where three won't fit) it
            becomes a swipeable carousel instead of squeezing into narrow columns. */}
        {visible.length === 0 ? (
          <TypographyMuted className="mt-16 text-center">{labels.empty}</TypographyMuted>
        ) : (
          <>
            <div className="mt-12 hidden gap-6 lg:grid lg:grid-cols-3 [&_img]:object-center">
              {visible.map((post, i) => (
                <Reveal key={post.slug} index={i} className="h-full">
                  <BlogPostCard post={post} />
                </Reveal>
              ))}
            </div>

            <Carousel
              key={active}
              opts={{ align: 'start', direction: locale === 'ar' ? 'rtl' : 'ltr' }}
              className="mt-10 lg:hidden [&_img]:object-center"
            >
              <CarouselContent className="-ml-4 py-2">
                {visible.map((post) => (
                  <CarouselItem key={post.slug} className="basis-[82%] pl-4 sm:basis-1/2">
                    <BlogPostCard post={post} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </>
        )}

        {/* View all — sand button flanked by mirrored leaf sprigs, one row */}
        <Reveal className="mt-14 flex flex-nowrap items-center justify-center gap-3 sm:gap-5">
          <Image
            src="/blog-leaf.webp"
            alt=""
            aria-hidden
            width={520}
            height={471}
            sizes="72px"
            className="pointer-events-none block h-9 w-auto shrink-0 -translate-y-2 select-none sm:h-12 sm:-translate-y-3 rtl:-scale-x-100"
          />
          <Button asChild variant="ghost" size="lg" className="shrink-0 bg-sand text-primary">
            <Link href="/blog">
              {labels.viewAll}
              <ArrowRight
                aria-hidden
                data-directional-icon-decorative
                className="rtl:-scale-x-100"
              />
            </Link>
          </Button>
          <Image
            src="/blog-leaf.webp"
            alt=""
            aria-hidden
            width={520}
            height={471}
            sizes="72px"
            className="pointer-events-none block h-9 w-auto shrink-0 -translate-y-2 -scale-x-100 select-none sm:h-12 sm:-translate-y-3 rtl:scale-x-100"
          />
        </Reveal>
      </div>
    </section>
  );
}
